import { ExternalLink, Shield } from "lucide-react"
import { Suspense } from "react"
import {
  getAgentCallContractorApprovalLabel,
  getArrangementLimitLabel,
  getHasDesignatedContractorLabel,
  getOperationRuleDetailPath,
  type OperationRule,
  resolveOperationRule,
} from "@/api/client/operation-rule"
import { ownerApi, ownerProperties } from "@/api/client/owner"
import { Card } from "@/components/ui/card"
import { ReactErrorBoundary } from "@/components/ui/error-boundary"
import { openInNewTab } from "@/lib/navigation"

interface PropertyOwnerInfoProps {
  owner?: {
    id?: string
    name?: string
    email?: string
    phoneNumber?: string
  }
  // 案件に紐付く物件。指定時は物件固有ルールを優先して表示する(E-13)
  propertyId?: number
}

type OperationRuleView = Pick<
  OperationRule,
  "agentCallContractorApproval" | "hasDesignatedContractor" | "arrangementLimit"
>

function OperationRulesGrid({ operationRule }: { operationRule?: OperationRuleView | null }) {
  return (
    <div className="grid grid-cols-3 gap-4">
      <div>
        <div className="text-xs text-slate-400 mb-1">AC業者手配</div>
        <div className="text-xs">
          {getAgentCallContractorApprovalLabel(operationRule?.agentCallContractorApproval)}
        </div>
      </div>
      <div>
        <div className="text-xs text-slate-400 mb-1">オーナー指定業者</div>
        <div className="text-xs">
          {getHasDesignatedContractorLabel(operationRule?.hasDesignatedContractor)}
        </div>
      </div>
      <div>
        <div className="text-xs text-slate-400 mb-1">手配上限金額</div>
        <div className="text-xs">{getArrangementLimitLabel(operationRule?.arrangementLimit)}</div>
      </div>
    </div>
  )
}

// 適用ルールのヘッダー。どのスコープが効いているかのラベルと、そのルール詳細ページへのリンクを出す。
function OperationRulesHeader({
  scopeLabel,
  detailPath,
}: {
  scopeLabel?: string
  detailPath?: string
}) {
  return (
    <div className="flex items-center gap-2 mb-3">
      <Shield className="size-5" />
      <span className="text-sm font-semibold">運用ルール設定</span>
      {scopeLabel && (
        <span className="rounded bg-slate-100 px-1.5 py-0.5 text-[10px] font-semibold text-slate-600">
          {scopeLabel}
        </span>
      )}
      {detailPath && (
        <ExternalLink
          className="size-4 cursor-pointer ml-auto"
          aria-label="運用ルール詳細を開く"
          onClick={() => openInNewTab(detailPath)}
        />
      )}
    </div>
  )
}

// 案件の物件に適用されるルールを「物件固有 → オーナー既定」で解決して表示する(E-13)。
// 解決結果の propertyId が null なら既定ルールへフォールバックしている。
function ResolvedOperationRules({ ownerId, propertyId }: { ownerId: string; propertyId?: number }) {
  const {
    data: { owner },
  } = ownerApi.useOwnerGetSuspense(ownerId, {
    withOperationRules: ownerProperties.OwnerGetWithOperationRules.true,
  })
  const operationRule = resolveOperationRule(owner.operationRules, propertyId)

  const scopeLabel = !operationRule
    ? "未設定"
    : operationRule.propertyId != null
      ? "物件個別ルール"
      : propertyId != null
        ? "既定ルールを適用"
        : "既定ルール"
  // 未設定でも既定ルールの詳細ページ(「設定されていません」表示)へ誘導する
  const detailPath = getOperationRuleDetailPath(ownerId, operationRule?.propertyId ?? null)

  return (
    <>
      <OperationRulesHeader scopeLabel={scopeLabel} detailPath={detailPath} />
      <OperationRulesGrid operationRule={operationRule} />
    </>
  )
}

export function PropertyOwnerInfo({ owner, propertyId }: PropertyOwnerInfoProps) {
  const ownerId = owner?.id
  const fallback = (
    <>
      <OperationRulesHeader
        detailPath={ownerId ? getOperationRuleDetailPath(ownerId, null) : undefined}
      />
      <OperationRulesGrid />
    </>
  )

  return (
    <Card className="p-0">
      <div className="bg-slate-100 border-b px-4 py-2 text-center">
        <span className="text-sm font-semibold">物件オーナー情報</span>
      </div>
      <div className="p-4 space-y-4">
        <div className="grid grid-cols-3 gap-4">
          <div>
            <div className="text-xs text-slate-400 mb-1">オーナー名</div>
            <div className="text-sm">
              {ownerId ? (
                <span
                  className="text-[#458CF7] cursor-pointer hover:opacity-80"
                  onClick={() => openInNewTab(`/owners/${ownerId}`)}
                >
                  {owner?.name || "-"}
                </span>
              ) : (
                owner?.name || "-"
              )}
            </div>
          </div>
          <div>
            <div className="text-xs text-slate-400 mb-1">メールアドレス</div>
            <div className="text-sm break-all">{owner?.email || "-"}</div>
          </div>
          <div>
            <div className="text-xs text-slate-400 mb-1">電話番号</div>
            <div className="text-sm">{owner?.phoneNumber || "-"}</div>
          </div>
        </div>

        <div className="border-t pt-4">
          {ownerId ? (
            <ReactErrorBoundary fallback={() => fallback}>
              <Suspense fallback={fallback}>
                <ResolvedOperationRules ownerId={ownerId} propertyId={propertyId} />
              </Suspense>
            </ReactErrorBoundary>
          ) : (
            fallback
          )}
        </div>
      </div>
    </Card>
  )
}
